import { createFileRoute } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { useState, useEffect } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

export const Route = createFileRoute('/_unauth/pending-access')({
  component: PendingAccessPage,
})

function PendingAccessPage() {
  const { logout } = useAuth()
  const [showDebug, setShowDebug] = useState(false)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [copied, setCopied] = useState(false)

  console.error('[PENDING-ACCESS] Page loaded!')

  // Get user email from localStorage if available
  const storedProfile = localStorage.getItem('user-profile')
  const userProfile = storedProfile ? JSON.parse(storedProfile) : null
  const userEmail = userProfile?.email || null

  console.error('[PENDING-ACCESS] User email:', userEmail)
  console.error('[PENDING-ACCESS] User profile:', userProfile)

  // Gather diagnostic information
  useEffect(() => {
    const gatherDebugInfo = async () => {
      try {
        const session = await fetchAuthSession()
        const cognitoSub = session.tokens?.idToken?.payload?.sub
        const tokenExpiry = session.tokens?.accessToken?.payload?.exp
        const now = Math.floor(Date.now() / 1000)

        // Read actual API error from auth context (stored during getUserProfile)
        let apiError: string | null = null
        let clockSkewDetected = false
        let clockSkewMinutes = 0

        const storedError = localStorage.getItem('auth-api-error')
        if (storedError) {
          try {
            const errorInfo = JSON.parse(storedError)
            apiError = errorInfo.message
            clockSkewDetected = errorInfo.isClockSkew || false

            // Try to extract time difference from error message
            // Format: "20260107T210755Z is still later than 20260107T210452Z"
            if (clockSkewDetected && apiError && typeof apiError === 'string') {
              const match = apiError.match(/(\d{8}T\d{6}Z).*?(\d{8}T\d{6}Z)/)
              if (match) {
                const clientTime = new Date(match[1].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'))
                const serverTime = new Date(match[2].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'))
                clockSkewMinutes = Math.round((clientTime.getTime() - serverTime.getTime()) / 60000)
              }
            }

            console.error('[PENDING-ACCESS] Using stored API error from auth context:', errorInfo)
          } catch (e) {
            console.error('[PENDING-ACCESS] Failed to parse stored error:', e)
          }
        }

        const info = {
          timestamp: new Date().toISOString(),
          userProfile: userProfile || 'No cached profile',
          cognitoSub: cognitoSub || 'No Cognito sub found',
          tokenExpiry: tokenExpiry ? new Date(tokenExpiry * 1000).toISOString() : 'No token',
          tokenValid: tokenExpiry ? tokenExpiry > now : false,
          timeUntilExpiry: tokenExpiry ? `${Math.floor((tokenExpiry - now) / 60)} minutes` : 'N/A',
          allLocalStorageKeys: Object.keys(localStorage).filter(k => !k.includes('Intercom')),
          sessionTokensPresent: !!session.tokens,
          identityId: session.identityId || 'No identity ID',
          apiError,
          clockSkewDetected,
          clockSkewMinutes: clockSkewDetected ? `${clockSkewMinutes > 0 ? '+' : ''}${clockSkewMinutes} minutes` : 'N/A'
        }

        setDebugInfo(info)
        console.error('[PENDING-ACCESS] Debug info:', info)
      } catch (error) {
        console.error('[PENDING-ACCESS] Error gathering debug info:', error)
        setDebugInfo({
          error: error instanceof Error ? error.message : 'Unknown error',
          userProfile: userProfile || 'No cached profile'
        })
      }
    }

    gatherDebugInfo()
  }, [])

  const copyDebugInfo = () => {
    if (debugInfo) {
      navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Acesso Pendente
            </h1>
            <p className="text-gray-600 text-sm mb-4">
              {userEmail && (
                <span className="block mb-2 text-gray-700 font-medium">{userEmail}</span>
              )}
              Sua autenticação foi bem-sucedida, mas você ainda não possui permissões de acesso no sistema.
            </p>
          </div>

          {/* Clock Skew Warning Banner */}
          {debugInfo?.clockSkewDetected && (
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-6 animate-pulse">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-red-900 mb-2">
                    Relógio do Sistema Dessinconizado
                  </h3>
                  <p className="text-sm text-red-800 mb-3">
                    O relógio do seu computador está <strong>{debugInfo.clockSkewMinutes}</strong> dessinconizado com o servidor.
                    Isso impede o acesso ao sistema.
                  </p>
                  <div className="bg-red-100 border border-red-300 rounded p-3 mb-3">
                    <p className="text-xs font-semibold text-red-900 mb-2">Como corrigir:</p>
                    <ol className="text-xs text-red-800 space-y-1 list-decimal list-inside">
                      <li>Abra as configurações de Data e Hora do Windows</li>
                      <li>Ative "Definir hora automaticamente"</li>
                      <li>Clique em "Sincronizar agora"</li>
                      <li>Feche todas as abas e faça login novamente</li>
                    </ol>
                  </div>
                  <p className="text-xs text-red-700 italic">
                    📌 Este é um problema de segurança do AWS que requer sincronização precisa de tempo.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="text-sm font-semibold text-blue-900 mb-2">
              O que fazer agora?
            </h2>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Entre em contato com o administrador do sistema</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Solicite que suas permissões e função sejam configuradas</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Assim que tiver acesso, faça login novamente</span>
              </li>
            </ul>
          </div>

          <button
            onClick={() => logout()}
            className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white font-medium py-3 px-4 rounded-lg transition-colors mb-4"
          >
            Voltar ao Login
          </button>

          {/* Debug Information Toggle */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="w-full text-sm text-gray-600 hover:text-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className={`w-4 h-4 transition-transform ${showDebug ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {showDebug ? 'Ocultar Informações Técnicas' : 'Mostrar Informações Técnicas'}
            </button>

            {showDebug && debugInfo && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Diagnóstico para Suporte
                  </h3>
                  <button
                    onClick={copyDebugInfo}
                    className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>

                <div className="space-y-2 text-xs font-mono">
                  <div className="flex flex-col">
                    <span className="text-gray-500">Timestamp:</span>
                    <span className="text-gray-800 break-all">{debugInfo.timestamp}</span>
                  </div>

                  {debugInfo.error ? (
                    <div className="flex flex-col">
                      <span className="text-red-500">Error:</span>
                      <span className="text-red-700 break-all">{debugInfo.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col">
                        <span className="text-gray-500">Cognito Sub:</span>
                        <span className="text-gray-800 break-all">{debugInfo.cognitoSub}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">User ID:</span>
                        <span className="text-gray-800 break-all">
                          {typeof debugInfo.userProfile === 'object'
                            ? debugInfo.userProfile?.userId || debugInfo.userProfile?.id || 'N/A'
                            : debugInfo.userProfile}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Status:</span>
                        <span className="text-gray-800 break-all">
                          {typeof debugInfo.userProfile === 'object'
                            ? debugInfo.userProfile?.status || 'N/A'
                            : 'N/A'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Role:</span>
                        <span className="text-gray-800 break-all">
                          {typeof debugInfo.userProfile === 'object'
                            ? debugInfo.userProfile?.role || debugInfo.userProfile?.roleName || 'N/A'
                            : 'N/A'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Token Válido:</span>
                        <span className={debugInfo.tokenValid ? 'text-green-600' : 'text-red-600'}>
                          {debugInfo.tokenValid ? 'Sim' : 'Não'} ({debugInfo.timeUntilExpiry})
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Identity ID:</span>
                        <span className="text-gray-800 break-all">{debugInfo.identityId}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Session Tokens:</span>
                        <span className={debugInfo.sessionTokensPresent ? 'text-green-600' : 'text-red-600'}>
                          {debugInfo.sessionTokensPresent ? 'Presentes' : 'Ausentes'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-gray-500">Clock Skew:</span>
                        <span className={debugInfo.clockSkewDetected ? 'text-red-600 font-bold' : 'text-green-600'}>
                          {debugInfo.clockSkewDetected ? `DETECTADO: ${debugInfo.clockSkewMinutes}` : 'OK'}
                        </span>
                      </div>

                      {debugInfo.apiError && (
                        <div className="flex flex-col">
                          <span className="text-gray-500">API Error:</span>
                          <span className="text-red-600 break-all text-xs">{debugInfo.apiError}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-gray-300">
                  <p className="text-xs text-gray-600">
                    <strong>Compartilhe essas informações</strong> com o suporte técnico para diagnóstico mais rápido.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
